import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import SidebarMenu from '../components/SidebarMenu';
import Footer from '../components/Footer';
import ProfileMenu from '../components/ProfileMenu';
import NotificationBell from '../components/NotificationBell';
import { MarketingNav } from '../components/ui';
import axios from 'axios';
import { getMenuItems } from '../menuItems';
import { acme, acmeMarketingPageSx, acmeIconTones } from '../theme/acmeTheme';

function PublicLayout({ authenticated, user, handleLogout, isImpersonating = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const menuItems = getMenuItems(authenticated, user).filter((item) => item.text !== 'Request Form');

  const guestLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
  ];

  const authLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
  ];

  const toggleDrawer = (open) => () => setMobileOpen(open);

  useEffect(() => {
    if (!authenticated) return undefined;
    axios
      .get('/api/notifications', { withCredentials: true })
      .then((res) => setNotifications(res.data || []))
      .catch((err) => console.error('Failed to fetch notifications:', err));
    return undefined;
  }, [authenticated]);

  const markNotificationAsRead = async (notificationId) => {
    try {
      const res = await axios.patch('/api/notifications/read', { notificationId }, { withCredentials: true });
      if (res.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n)),
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const authRightSlot = authenticated ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <NotificationBell
        notifications={notifications}
        onMarkAsRead={markNotificationAsRead}
        user={user}
        handleLogout={handleLogout}
        iconColor={acmeIconTones.default}
      />
      <ProfileMenu user={user} handleLogout={handleLogout} navTone="light" />
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        ...acmeMarketingPageSx,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: acme.colors.pageBg,
        pt: isImpersonating ? 6 : 0,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: isImpersonating ? 48 : 0,
          zIndex: 1100,
          pt: 2,
          px: { xs: 1.5, md: 2 },
          backgroundColor: acme.colors.pageBg,
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <MarketingNav
            links={authenticated ? authLinks : guestLinks}
            authenticated={authenticated}
            rightSlot={authRightSlot}
          />
        </Box>
      </Box>

      {authenticated && (
        <SidebarMenu
          menuItems={menuItems}
          mobileOpen={mobileOpen}
          toggleDrawer={toggleDrawer}
          isAuthenticated={authenticated}
          handleLogout={handleLogout}
        />
      )}

      <Box component="main" sx={{ flex: 1, px: { xs: 1.5, md: 2 }, py: { xs: 2, md: 3 } }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <Outlet
            context={{
              authenticated,
              user,
              handleLogout,
              notifications,
              markNotificationAsRead,
            }}
          />
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}

export default PublicLayout;
